// Upload page — dedicated intake surface, moved out of the Home dashboard so
// the home view can focus on throughput / dashboard data. Everything here was
// previously in Home.tsx.

import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiPost } from "../lib/api";
import type { IntakeMode, TargetPms } from "@shared/types";
import { TARGET_PMS_LABEL, allowedPmsForStore } from "@shared/types";

const STORAGE_STORE_KEY = "smartrx-entry.currentStoreCode";
const STORAGE_MODE_KEY  = "smartrx-entry.intakeMode";
const STORAGE_PMS_KEY   = "smartrx-entry.targetPms";

// Community Pharmacy store codes — only these run vaccine intake (PioneerRx sites).
const VACCINE_STORE_CODES = new Set(["COR", "DEN", "FM"]);

function getCurrentStoreCode(): string {
  return (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_STORE_KEY)) || "GV";
}

function getInitialMode(storeCode: string): IntakeMode {
  const saved = typeof window !== "undefined" && window.localStorage.getItem(STORAGE_MODE_KEY);
  if (saved === "vaccine" && VACCINE_STORE_CODES.has(storeCode)) return "vaccine";
  return "rx";
}

function getInitialPms(storeCode: string): TargetPms {
  const allowed = allowedPmsForStore(storeCode);
  const saved = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_PMS_KEY) as TargetPms | null : null;
  return saved && allowed.includes(saved) ? saved : allowed[0];
}

export function Upload() {
  const navigate = useNavigate();
  const storeCode = getCurrentStoreCode();
  const vaccineAllowed = VACCINE_STORE_CODES.has(storeCode);
  const allowedPms = allowedPmsForStore(storeCode);
  const [mode, setMode] = useState<IntakeMode>(() => getInitialMode(storeCode));
  const [targetPms, setTargetPms] = useState<TargetPms>(() => getInitialPms(storeCode));

  function selectPms(next: TargetPms) {
    setTargetPms(next);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_PMS_KEY, next);
  }
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function selectMode(next: IntakeMode) {
    setMode(next);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_MODE_KEY, next);
  }

  async function handleFile(file: File) {
    setUploadError(null);
    if (!["application/pdf", "image/png", "image/jpeg"].includes(file.type)) {
      setUploadError("Only PDF, PNG, or JPG are supported.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setUploadError("File too large (max 20 MB).");
      return;
    }
    setUploading(true);
    try {
      try {
        const blobUrl = URL.createObjectURL(file);
        window.sessionStorage.setItem("demo.lastUpload.url", blobUrl);
        window.sessionStorage.setItem("demo.lastUpload.name", file.name);
        window.sessionStorage.setItem("demo.lastUpload.mime", file.type);
      } catch { /* ignore */ }

      const base64 = await fileToBase64(file);
      const { rxIds } = await apiPost<{ rxIds: number[] }>("/intakes/upload", {
        storeCode,
        mode,
        targetPms,
        filename: file.name,
        contentType: file.type,
        base64,
      });
      if (rxIds.length > 0) navigate(`/review/${rxIds[0]}`);
      else navigate("/review-queue");
    } catch (e) {
      setUploadError(String(e));
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) void handleFile(file);
  }

  const dropTitle    = mode === "vaccine" ? "Drop a vaccine consent form" : "Drop a faxed or written prescription";
  const dropSubtitle = mode === "vaccine"
    ? "PDF, PNG, or JPG (max 20 MB). The AI extracts patient, vaccine, and Medicare fields — reviewer verifies anything under the auto-approve threshold."
    : "PDF, PNG, or JPG (max 20 MB). The AI extracts fields and routes the Rx to the review queue.";

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-1">Upload</h2>
      <p className="text-ink-soft mb-6">
        Drag in a scanned prescription or vaccine consent form. The AI extracts fields and sends the record to the review queue.
      </p>

      <div className="flex gap-3 items-center mb-4">
        {vaccineAllowed && (
          <div className="inline-flex rounded border border-border overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => selectMode("rx")}
              className={`px-4 py-1.5 ${mode === "rx" ? "bg-accent text-white" : "bg-surface text-ink-soft hover:bg-pill"}`}
            >
              New Rx
            </button>
            <button
              type="button"
              onClick={() => selectMode("vaccine")}
              className={`px-4 py-1.5 border-l border-border ${mode === "vaccine" ? "bg-accent text-white" : "bg-surface text-ink-soft hover:bg-pill"}`}
            >
              New Vaccine
            </button>
          </div>
        )}

        <div className="inline-flex items-center gap-2 text-sm">
          <span className="text-ink-soft">Processing software:</span>
          <select value={targetPms} onChange={(e) => selectPms(e.target.value as TargetPms)}
            className="border border-border rounded px-2 py-1 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-accent">
            {allowedPms.map((p) => <option key={p} value={p}>{TARGET_PMS_LABEL[p]}</option>)}
          </select>
        </div>
      </div>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="bg-accent-soft border border-dashed border-accent rounded-lg p-10 text-center"
      >
        <h3 className="text-accent font-semibold mb-1 text-lg">{dropTitle}</h3>
        <p className="text-ink-soft text-sm mb-4">{dropSubtitle}</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf,image/png,image/jpeg"
          hidden
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = ""; }}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className="px-5 py-2 bg-white border border-accent text-accent rounded text-sm font-medium hover:bg-accent-soft disabled:opacity-40"
        >
          {uploading ? "Uploading + extracting…" : "Choose file…"}
        </button>
        {uploadError && <p className="text-bad text-sm mt-4">{uploadError}</p>}
      </div>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") return reject(new Error("FileReader returned non-string"));
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
